import type { UnifiedAuthStep } from '~kui/types/auth';
import type { Ref } from 'vue';

import type { EntryCta, EntrySource, SiteRoleType } from '~/graphql/generated/graphql';
import type { CompletedFlowResult } from '~/types/login';

import { ClusterTermsRequirement } from '~/graphql/generated/graphql';

import { useSetPasswordWithToken } from './useSetPasswordWithToken';

interface AuthPasswordSetupOptions {
  roleType: SiteRoleType;
  loading: Ref<boolean>;
  goToStep: (step: UnifiedAuthStep) => void;
  entrySource: EntrySource | null;
  entryCta: EntryCta | null;
  /** The door's own words for a spent token (`loginDoorPasswordSetupCopy`). */
  tokenExpiredKey: string;
}

/**
 * The create-a-password step a fresh signup is offered, split out the way
 * `useAuthPasswordLogin` extracts the login half. The owner flow keeps the signup
 * itself and hands the token it came back with to `goToPostSignupStep`.
 */
export const useAuthPasswordSetup = ({ roleType, loading, goToStep, entrySource, entryCta, tokenExpiredKey }: AuthPasswordSetupOptions) => {
  const { $analytics } = useNuxtApp();
  const configStore = useConfigStore();
  const { user } = useAuth();
  const { t } = useI18n();

  const passwordChangeToken = ref<string | null>(null);

  const goToAccountReadyStep = () => goToStep(configStore.isSchoolsCluster ? 'school' : 'welcome');

  /** Set while the step is resetting a forgotten password rather than adding a first one. */
  let finishPasswordReset: (() => Promise<CompletedFlowResult>) | null = null;
  /** How the reset step was first left; a retry after a refused sign-in reports the same outcome. */
  let resetSkipped: boolean | null = null;

  const leavePasswordSetup = (skipped: boolean) => {
    passwordChangeToken.value = null;
    if (finishPasswordReset) {
      const finish = finishPasswordReset;
      resetSkipped ??= skipped;
      const outcomeSkipped = resetSkipped;
      // Held while the sign-in finishes, so a second skip or save meanwhile has nothing to act on.
      loading.value = true;
      void finish().then((result) => {
        // A refused sign-in keeps the step in reset mode: trying again retries the sign-in, never a signup.
        if (!result.error) {
          finishPasswordReset = null;
          resetSkipped = null;
          $analytics.track(EVENTS.LOGIN_PASSWORD_SETUP_COMPLETED, { skipped: outcomeSkipped, roleType });
        }
      });
      return;
    }
    $analytics.track(EVENTS.UNIFIED_AUTH_PASSWORD_SETUP_COMPLETED, {
      skipped,
      entrySource,
      entryCta,
    });
    goToAccountReadyStep();
  };

  const { expiredTokenMessage, setPassword } = useSetPasswordWithToken({
    loading,
    token: passwordChangeToken,
    tokenExpiredMessage: () => t(tokenExpiredKey),
    onSaved: () => leavePasswordSetup(false),
  });

  /**
   * Signup hands back a single-use token while password login is enabled, so the student can
   * add a first password without a second emailed code. No token means the step is not offered.
   * Neither is it while terms are still pending: a password is only created once they are
   * accepted (signup records the schools-cluster enrolment itself, so this only holds it back
   * should that ever stop being the case).
   */
  const goToPostSignupStep = (token: string | null | undefined) => {
    finishPasswordReset = null;
    resetSkipped = null;
    expiredTokenMessage.value = null;
    const termsPending = user.value?.clusterTermsRequirement
      && user.value.clusterTermsRequirement !== ClusterTermsRequirement.None;
    passwordChangeToken.value = termsPending ? null : (token ?? null);
    if (passwordChangeToken.value) {
      goToStep('password-setup');
      return;
    }
    goToAccountReadyStep();
  };

  /**
   * A password reset on the schools site signs in an existing account and hands back a token for
   * the new password. The same step takes it, and leaving it, saved or skipped, finishes that sign-in.
   */
  const goToPasswordResetStep = (token: string, finish: () => Promise<CompletedFlowResult>) => {
    expiredTokenMessage.value = null;
    passwordChangeToken.value = token;
    finishPasswordReset = finish;
    resetSkipped = null;
    goToStep('password-setup');
  };

  /**
   * Once a reset's sign-in has been attempted and refused, the token is gone, so Save retries the
   * sign-in the way Skip does. Before any attempt a missing token means the server refused it as
   * spent, and Save keeps explaining that.
   */
  const submitPasswordSetup = (newPassword: string) => {
    if (finishPasswordReset && resetSkipped !== null) {
      leavePasswordSetup(false);
      return Promise.resolve({ error: null });
    }
    return setPassword(newPassword);
  };

  const skipPasswordSetup = () => leavePasswordSetup(true);

  return {
    passwordChangeToken,
    goToPostSignupStep,
    goToPasswordResetStep,
    submitPasswordSetup,
    skipPasswordSetup,
  };
};
